import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import {
  Box, Typography, IconButton, TextField, Button, Menu, MenuItem, ListItemIcon,
  CircularProgress, Alert, Dialog, DialogContent, DialogActions, Chip, Skeleton, LinearProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import { chatAPI } from '../../services/api';
import { clearChatNotifications } from '../../services/push';
import resizeImage from '../../pages/technician/resizeImage';
import useAudioRecorder, { MAX_AUDIO_SECONDS } from './useAudioRecorder';

/**
 * Conversacion de un proyecto, al estilo WhatsApp.
 *
 * La usan la app movil (/tecnicos, a pantalla completa) y la pestana Chat del
 * proyecto en el gestor. Ocupa todo el alto de su contenedor: quien la monta
 * decide el tamano.
 *
 * No hay WebSocket: con la pantalla visible se pregunta cada pocos segundos
 * por los mensajes nuevos (`after_id`), y cuando la app esta cerrada avisan las
 * notificaciones push. Asi no hay que tocar el proxy de app.js ni el de Plesk.
 */

const POLL_MS = 4000;
// Mismo limite que el backend (MAX_ATTACHMENT_BYTES en chat.py)
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const CHAT_BG = '#efeae2';
const MINE_BG = '#d9fdd3';

const DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv,.zip,.rar,.7z,.dwg,.dxf,.step,.stp';

// Colores para el nombre del autor, fijos por persona como en los grupos
const AUTHOR_COLORS = ['#1e88e5', '#8e24aa', '#00897b', '#f4511e', '#6d4c41', '#3949ab', '#827717', '#d81b60'];
const authorColor = (author) => {
  const key = `${author.kind}-${author.id}`;
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) % 997;
  return AUTHOR_COLORS[h % AUTHOR_COLORS.length];
};

const formatTime = (iso) => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (secs) => {
  const s = Math.round(secs || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// Los enlaces van dentro del texto: se pintan como enlace sin guardarlos aparte
const URL_RE = /(https?:\/\/[^\s<]+)/g;
function Linkified({ text }) {
  const parts = (text || '').split(URL_RE);
  return parts.map((part, i) => (i % 2 === 1
    ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        style={{ color: '#027eb5', wordBreak: 'break-all' }}>{part}</a>
    : <React.Fragment key={i}>{part}</React.Fragment>));
}

/** Descarga un adjunto con el token y lo deja en un object URL. */
function useChatBlob(filePath, enabled = true) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState(filePath && enabled ? 'loading' : 'idle');

  useEffect(() => {
    if (!filePath || !enabled) return undefined;
    let objectUrl = null;
    let cancelled = false;
    setState('loading');
    chatAPI.fileBlob(filePath)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
        setState('ok');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filePath, enabled]);

  return { url, state };
}

async function downloadAttachment(msg) {
  const res = await chatAPI.fileBlob(msg.file_path);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = msg.file_name || 'fichero';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function PhotoContent({ msg, onOpen }) {
  const { url, state } = useChatBlob(msg.file_path);
  if (state === 'loading') return <Skeleton variant="rectangular" width={240} height={180} sx={{ borderRadius: 1 }} />;
  if (state !== 'ok') return <Typography variant="body2" color="text.secondary">Foto no disponible</Typography>;
  return (
    <Box component="img" src={url} alt={msg.file_name || 'Foto'} onClick={() => onOpen(url)}
      sx={{ display: 'block', maxWidth: '100%', width: 260, maxHeight: 320, objectFit: 'cover',
            borderRadius: 1, cursor: 'zoom-in' }} />
  );
}

function AudioContent({ msg }) {
  const { url, state } = useChatBlob(msg.file_path);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}>
      <MicIcon fontSize="small" color="action" />
      {state === 'ok'
        ? <Box component="audio" controls preload="metadata" src={url} sx={{ height: 36, width: 230, maxWidth: '100%' }} />
        : state === 'loading'
          ? <CircularProgress size={18} />
          : <Typography variant="body2" color="text.secondary">Audio no disponible</Typography>}
      {msg.duration ? (
        <Typography variant="caption" color="text.secondary">{formatDuration(msg.duration)}</Typography>
      ) : null}
    </Box>
  );
}

// El video no se descarga hasta que se pide: puede pesar decenas de MB y en
// obra se esta casi siempre con datos moviles.
function VideoContent({ msg }) {
  const [wanted, setWanted] = useState(false);
  const { url, state } = useChatBlob(msg.file_path, wanted);
  if (!wanted) {
    return (
      <Box onClick={() => setWanted(true)}
        sx={{ width: 240, height: 150, borderRadius: 1, bgcolor: '#263238', color: '#fff',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer' }}>
        <PlayCircleIcon sx={{ fontSize: 48 }} />
        <Typography variant="caption">Video · {formatSize(msg.file_size)}</Typography>
      </Box>
    );
  }
  if (state === 'loading') return <Skeleton variant="rectangular" width={240} height={150} sx={{ borderRadius: 1 }} />;
  if (state !== 'ok') return <Typography variant="body2" color="text.secondary">Video no disponible</Typography>;
  return <Box component="video" controls autoPlay src={url} sx={{ display: 'block', width: 260, maxWidth: '100%', borderRadius: 1 }} />;
}

function FileContent({ msg }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadAttachment(msg); } finally { setBusy(false); }
  };
  return (
    <Box onClick={download}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, minWidth: 200, borderRadius: 1,
            bgcolor: 'rgba(0,0,0,0.05)', cursor: 'pointer' }}>
      {busy ? <CircularProgress size={24} /> : <InsertDriveFileIcon color="primary" />}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontWeight: 500 }}>{msg.file_name}</Typography>
        <Typography variant="caption" color="text.secondary">{formatSize(msg.file_size)}</Typography>
      </Box>
    </Box>
  );
}

function MessageBubble({ msg, showAuthor, onOpenPhoto, onMenu }) {
  const mine = msg.is_mine;
  return (
    <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 0.5, px: 1 }}>
      <Box
        onContextMenu={(e) => { e.preventDefault(); onMenu(e.currentTarget, msg); }}
        sx={{
          maxWidth: '82%', bgcolor: mine ? MINE_BG : '#fff', borderRadius: 2,
          borderTopRightRadius: mine && showAuthor ? 0 : undefined,
          borderTopLeftRadius: !mine && showAuthor ? 0 : undefined,
          px: 1, pt: 0.5, pb: 0.25, boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
          userSelect: 'text', WebkitTouchCallout: 'none',
        }}
      >
        {showAuthor && !mine && (
          <Typography variant="caption" sx={{ fontWeight: 600, color: authorColor(msg.author), display: 'block' }}>
            {msg.author.name}
          </Typography>
        )}

        {msg.deleted ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BlockIcon sx={{ fontSize: 16 }} /> Mensaje eliminado
          </Typography>
        ) : (
          <>
            {msg.kind === 'photo' && <PhotoContent msg={msg} onOpen={onOpenPhoto} />}
            {msg.kind === 'audio' && <AudioContent msg={msg} />}
            {msg.kind === 'video' && <VideoContent msg={msg} />}
            {msg.kind === 'file' && <FileContent msg={msg} />}
            {msg.body && (
              <Typography variant="body2" component="div"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: msg.kind === 'text' ? 0 : 0.5 }}>
                <Linkified text={msg.body} />
              </Typography>
            )}
          </>
        )}

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.4 }}>
          {formatTime(msg.created_at)}
        </Typography>
      </Box>
    </Box>
  );
}

/** Envio en curso: se pinta como un mensaje propio con su progreso. */
function PendingBubble({ item }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, px: 1 }}>
      <Box sx={{ bgcolor: MINE_BG, borderRadius: 2, px: 1.5, py: 1, minWidth: 180, opacity: 0.85 }}>
        <Typography variant="body2">{item.label}</Typography>
        {item.error ? (
          <Typography variant="caption" color="error">{item.error}</Typography>
        ) : (
          <LinearProgress variant={item.progress ? 'determinate' : 'indeterminate'} value={item.progress || 0} sx={{ mt: 0.5 }} />
        )}
      </Box>
    </Box>
  );
}

export default function ProjectChat({ projectId, header = null, onActivity = null }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [attachAnchor, setAttachAnchor] = useState(null);
  const [menu, setMenu] = useState(null);           // { anchor, msg }
  const [viewer, setViewer] = useState(null);       // object URL de la foto ampliada
  const [preview, setPreview] = useState(null);     // { files: [{file, kind, url}], caption }

  const scrollerRef = useRef(null);
  const unreadRef = useRef(null);
  const lastIdRef = useRef(0);
  const markedRef = useRef(0);
  const stickToBottomRef = useRef(true);
  const restoreScrollRef = useRef(null);
  const scrollToUnreadRef = useRef(false);

  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const galleryRef = useRef(null);
  const docRef = useRef(null);
  const audioFileRef = useRef(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const recorder = useAudioRecorder();

  const describe = (err, fallback) => err.response?.data?.detail || fallback;

  const markRead = useCallback((lastId) => {
    if (!lastId || lastId <= markedRef.current) return;
    if (document.visibilityState !== 'visible') return;
    markedRef.current = lastId;
    chatAPI.markRead(projectId, lastId)
      .then(() => onActivityRef.current && onActivityRef.current())
      .catch(() => { markedRef.current = 0; });
    clearChatNotifications(projectId);
  }, [projectId]);

  const appendMessages = useCallback((incoming) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !seen.has(m.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
    const maxId = Math.max(...incoming.map((m) => m.id));
    if (maxId > lastIdRef.current) lastIdRef.current = maxId;
  }, []);

  // Carga inicial
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    lastIdRef.current = 0;
    markedRef.current = 0;
    chatAPI.messages(projectId)
      .then((res) => {
        if (cancelled) return;
        const { messages: list, has_more: more, last_read_message_id: lastRead } = res.data;
        const firstUnread = list.find((m) => m.id > (lastRead || 0) && !m.is_mine);
        setFirstUnreadId(firstUnread ? firstUnread.id : null);
        scrollToUnreadRef.current = !!firstUnread;
        setMessages(list);
        setHasMore(more);
        lastIdRef.current = list.length ? list[list.length - 1].id : 0;
        setError('');
        markRead(lastIdRef.current);
      })
      .catch((err) => { if (!cancelled) setError(describe(err, 'No se ha podido cargar el chat')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, markRead]);

  const poll = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    try {
      const res = await chatAPI.messages(projectId, { after_id: lastIdRef.current });
      appendMessages(res.data.messages);
      markRead(lastIdRef.current);
    } catch (err) {
      // Un fallo puntual (cobertura) no se muestra: el siguiente sondeo lo arregla
    }
  }, [projectId, appendMessages, markRead]);

  // Sondeo con la pantalla visible, y al instante si llega un push de este chat
  useEffect(() => {
    if (loading) return undefined;
    const timer = setInterval(poll, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    const onSwMessage = (event) => {
      if (event.data?.type === 'chat-push' && Number(event.data.data?.project_id) === Number(projectId)) poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [loading, poll, projectId]);

  // Desplazamiento: abajo del todo, salvo que el usuario este leyendo arriba
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (restoreScrollRef.current !== null) {
      el.scrollTop = el.scrollHeight - restoreScrollRef.current;
      restoreScrollRef.current = null;
      return;
    }
    if (scrollToUnreadRef.current && unreadRef.current) {
      scrollToUnreadRef.current = false;
      el.scrollTop = unreadRef.current.offsetTop - 60;
      return;
    }
    if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, pending, loading]);

  const onScroll = () => {
    const el = scrollerRef.current;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const loadOlder = async () => {
    if (!messages.length) return;
    setLoadingOlder(true);
    try {
      const res = await chatAPI.messages(projectId, { before_id: messages[0].id });
      restoreScrollRef.current = scrollerRef.current.scrollHeight - scrollerRef.current.scrollTop;
      setMessages((prev) => [...res.data.messages, ...prev]);
      setHasMore(res.data.has_more);
    } catch (err) {
      setError(describe(err, 'No se han podido cargar los mensajes anteriores'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const afterOwnMessage = (msg) => {
    stickToBottomRef.current = true;
    setFirstUnreadId(null);
    appendMessages([msg]);
    markedRef.current = Math.max(markedRef.current, msg.id);
    if (onActivityRef.current) onActivityRef.current();
  };

  const sendText = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const res = await chatAPI.send(projectId, body);
      setText('');
      afterOwnMessage(res.data);
    } catch (err) {
      setError(describe(err, 'No se ha podido enviar el mensaje'));
    } finally {
      setSending(false);
    }
  };

  const upload = async ({ file, kind, caption, duration, label }) => {
    const key = `${Date.now()}-${Math.random()}`;
    setPending((p) => [...p, { key, label, progress: 0 }]);
    stickToBottomRef.current = true;
    const form = new FormData();
    form.append('file', file, file.name || label);
    if (kind) form.append('kind', kind);
    if (caption) form.append('caption', caption);
    if (duration) form.append('duration', String(duration));
    try {
      const res = await chatAPI.upload(projectId, form, (e) => {
        if (!e.total) return;
        const progress = Math.round((e.loaded / e.total) * 100);
        setPending((p) => p.map((it) => (it.key === key ? { ...it, progress } : it)));
      });
      setPending((p) => p.filter((it) => it.key !== key));
      afterOwnMessage(res.data);
    } catch (err) {
      const msg = describe(err, 'No se ha podido enviar');
      setPending((p) => p.map((it) => (it.key === key ? { ...it, error: msg } : it)));
      setTimeout(() => setPending((p) => p.filter((it) => it.key !== key)), 6000);
    }
  };

  // Ficheros elegidos: se previsualizan con un pie, como en WhatsApp
  const onFilesChosen = (event, forcedKind) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setPreview({
      caption: '',
      files: files.map((file) => {
        const kind = forcedKind
          || (file.type.startsWith('image/') ? 'photo' : file.type.startsWith('video/') ? 'video' : 'file');
        return { file, kind, url: kind === 'photo' || kind === 'video' ? URL.createObjectURL(file) : null };
      }),
    });
  };

  const closePreview = () => {
    preview?.files.forEach((f) => f.url && URL.revokeObjectURL(f.url));
    setPreview(null);
  };

  const sendPreview = async () => {
    const { files, caption } = preview;
    closePreview();
    for (let i = 0; i < files.length; i += 1) {
      const { file, kind } = files[i];
      // Se avisa antes de subir: con datos moviles no tiene sentido esperar a
      // que el servidor lo rechace despues de mandar 80 MB
      if (file.size > MAX_UPLOAD_BYTES) {
        setError(`"${file.name}" pesa ${formatSize(file.size)}; el maximo es ${formatSize(MAX_UPLOAD_BYTES)}.`);
        continue;
      }
      // Las fotos se reducen en el movil antes de subirlas, como las de las tareas
      const toSend = kind === 'photo' ? await resizeImage(file) : file;
      const label = { photo: '📷 Foto', video: '🎥 Video', file: `📎 ${file.name}` }[kind];
      upload({ file: toSend, kind, caption: i === 0 ? caption.trim() : '', label });
    }
  };

  // Plan B de las notas de voz: la grabadora del propio movil. Con capture,
  // Android abre su app de grabacion y devuelve el fichero, sin depender de
  // que el navegador consiga el microfono.
  const onAudioFileChosen = (event) => {
    const file = (event.target.files || [])[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`El audio pesa ${formatSize(file.size)}; el maximo es ${formatSize(MAX_UPLOAD_BYTES)}.`);
      return;
    }
    recorder.setError('');
    upload({ file, kind: 'audio', label: '🎤 Audio' });
  };

  const startRecording = async () => {
    stickToBottomRef.current = true;
    await recorder.start();
  };

  const sendRecording = useCallback(async () => {
    const result = await recorder.stop();
    if (!result || result.duration < 0.8) return;   // un toque sin querer no es un audio
    const ext = result.mimeType.includes('mp4') ? 'm4a' : result.mimeType.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([result.blob], `audio.${ext}`, { type: result.mimeType });
    upload({ file, kind: 'audio', duration: result.duration, label: '🎤 Audio' });
  }, [recorder, upload]);

  useEffect(() => {
    if (recorder.recording && recorder.seconds >= MAX_AUDIO_SECONDS) sendRecording();
  }, [recorder.recording, recorder.seconds, sendRecording]);

  const deleteMessage = async (msg) => {
    setMenu(null);
    if (!window.confirm('¿Eliminar este mensaje para todos? Quedara la marca de "Mensaje eliminado".')) return;
    try {
      const res = await chatAPI.delete(msg.id);
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? res.data : m)));
    } catch (err) {
      setError(describe(err, 'No se ha podido eliminar'));
    }
  };

  const copyMessage = (msg) => {
    setMenu(null);
    navigator.clipboard?.writeText(msg.body || '');
  };

  // Separadores de dia y de no leidos
  const rendered = [];
  let lastDay = null;
  let prevAuthorKey = null;
  messages.forEach((msg) => {
    const day = new Date(msg.created_at).toDateString();
    if (day !== lastDay) {
      rendered.push(
        <Box key={`d-${msg.id}`} sx={{ textAlign: 'center', my: 1 }}>
          <Chip size="small" label={dayLabel(msg.created_at)}
            sx={{ bgcolor: '#fff', boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)', textTransform: 'capitalize' }} />
        </Box>,
      );
      lastDay = day;
      prevAuthorKey = null;
    }
    if (msg.id === firstUnreadId) {
      rendered.push(
        <Box key="unread" ref={unreadRef} sx={{ textAlign: 'center', my: 1, bgcolor: 'rgba(255,255,255,0.6)', py: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>Mensajes no leidos</Typography>
        </Box>,
      );
      prevAuthorKey = null;
    }
    const authorKey = `${msg.author.kind}-${msg.author.id}`;
    rendered.push(
      <MessageBubble key={msg.id} msg={msg} showAuthor={authorKey !== prevAuthorKey}
        onOpenPhoto={setViewer} onMenu={(anchor, m) => setMenu({ anchor, msg: m })} />,
    );
    prevAuthorKey = authorKey;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: CHAT_BG }}>
      {header}

      <Box ref={scrollerRef} onScroll={onScroll} sx={{ flex: 1, overflowY: 'auto', py: 1, minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {hasMore && (
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Button size="small" onClick={loadOlder} disabled={loadingOlder} sx={{ bgcolor: '#fff' }}>
                  {loadingOlder ? 'Cargando...' : 'Ver mensajes anteriores'}
                </Button>
              </Box>
            )}
            {!messages.length && !pending.length && (
              <Box sx={{ textAlign: 'center', mt: 4, px: 3 }}>
                <Typography variant="body2" sx={{ bgcolor: '#fff8c4', display: 'inline-block', px: 2, py: 1, borderRadius: 2 }}>
                  Todavia no hay mensajes. Lo que se escriba aqui queda guardado en el proyecto.
                </Typography>
              </Box>
            )}
            {rendered}
            {pending.map((item) => <PendingBubble key={item.key} item={item} />)}
          </>
        )}
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 0 }}>{error}</Alert>}
      {recorder.error && (
        <Alert severity="warning" onClose={() => recorder.setError('')} sx={{ borderRadius: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => audioFileRef.current?.click()}>
              Usar la grabadora
            </Button>
          }>
          {recorder.error} Puedes grabar con la grabadora del movil.
        </Alert>
      )}

      {/* Barra de escritura */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, p: 0.75, bgcolor: '#f0f2f5',
                 pb: 'calc(6px + env(safe-area-inset-bottom))' }}>
        {recorder.recording ? (
          <>
            <IconButton color="error" onClick={recorder.cancel} aria-label="Descartar audio"><DeleteIcon /></IconButton>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1, height: 48 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main',
                         animation: 'gpiBlink 1s infinite', '@keyframes gpiBlink': { '50%': { opacity: 0.2 } } }} />
              <Typography variant="body1">{formatDuration(recorder.seconds)}</Typography>
              <Typography variant="caption" color="text.secondary">Grabando...</Typography>
            </Box>
            <IconButton color="primary" onClick={sendRecording} aria-label="Enviar audio"
              sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
              <SendIcon />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton onClick={(e) => setAttachAnchor(e.currentTarget)} aria-label="Adjuntar"><AttachFileIcon /></IconButton>
            <IconButton onClick={() => cameraRef.current?.click()} aria-label="Hacer foto"><PhotoCameraIcon /></IconButton>
            <TextField
              fullWidth multiline maxRows={5} size="small" placeholder="Mensaje"
              value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                // En el escritorio Intro envia y Mayus+Intro hace salto de linea.
                // En el movil Intro siempre es salto de linea: hay boton de enviar.
                const touch = window.matchMedia('(pointer: coarse)').matches;
                if (e.key === 'Enter' && !e.shiftKey && !touch) {
                  e.preventDefault();
                  sendText();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 5 } }}
            />
            {text.trim() ? (
              <IconButton onClick={sendText} disabled={sending} aria-label="Enviar"
                sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
                {sending ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
              </IconButton>
            ) : (
              <IconButton onClick={startRecording} aria-label="Grabar audio" disabled={recorder.starting}
                sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' },
                      '&.Mui-disabled': { bgcolor: 'primary.light', color: '#fff' } }}>
                {recorder.starting ? <CircularProgress size={22} color="inherit" /> : <MicIcon />}
              </IconButton>
            )}
          </>
        )}
      </Box>

      {/* Dos entradas de camara y una de galeria: capture abre la camara del
          movil directamente, y sin capture es lo unico que deja elegir lo que
          ya esta en el telefono (mismo criterio que en las tareas). */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => onFilesChosen(e, 'photo')} />
      <input ref={videoRef} type="file" accept="video/*" capture="environment" hidden onChange={(e) => onFilesChosen(e, 'video')} />
      <input ref={galleryRef} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => onFilesChosen(e)} />
      <input ref={docRef} type="file" accept={DOC_ACCEPT} multiple hidden onChange={(e) => onFilesChosen(e, 'file')} />
      <input ref={audioFileRef} type="file" accept="audio/*" capture hidden onChange={onAudioFileChosen} />

      <Menu anchorEl={attachAnchor} open={!!attachAnchor} onClose={() => setAttachAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {[
          { label: 'Hacer foto', icon: <PhotoCameraIcon />, ref: cameraRef },
          { label: 'Grabar video', icon: <VideocamIcon />, ref: videoRef },
          { label: 'Galeria', icon: <PhotoLibraryIcon />, ref: galleryRef },
          { label: 'Documento', icon: <InsertDriveFileIcon />, ref: docRef },
          { label: 'Nota de voz (grabadora del movil)', icon: <KeyboardVoiceIcon />, ref: audioFileRef },
        ].map((opt) => (
          <MenuItem key={opt.label} onClick={() => { setAttachAnchor(null); opt.ref.current?.click(); }}>
            <ListItemIcon>{opt.icon}</ListItemIcon>{opt.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Menu de un mensaje: clic derecho en el escritorio, pulsacion larga en el movil */}
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu?.msg.body && !menu.msg.deleted && (
          <MenuItem onClick={() => copyMessage(menu.msg)}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>Copiar texto
          </MenuItem>
        )}
        {menu?.msg.file_path && !menu.msg.deleted && (
          <MenuItem onClick={() => { const m = menu.msg; setMenu(null); downloadAttachment(m); }}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>Descargar
          </MenuItem>
        )}
        {menu?.msg.can_delete && (
          <MenuItem onClick={() => deleteMessage(menu.msg)} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Eliminar
          </MenuItem>
        )}
        {menu && <MenuItem disabled>{menu.msg.author.name} · {formatTime(menu.msg.created_at)}</MenuItem>}
      </Menu>

      {/* Foto a pantalla completa */}
      <Dialog open={!!viewer} onClose={() => setViewer(null)} fullScreen
        PaperProps={{ sx: { bgcolor: '#000' } }}>
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <IconButton onClick={() => setViewer(null)} sx={{ color: '#fff' }} aria-label="Cerrar"><CloseIcon /></IconButton>
        </Box>
        <Box onClick={() => setViewer(null)} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {viewer && <Box component="img" src={viewer} alt="" sx={{ maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain' }} />}
        </Box>
      </Dialog>

      {/* Previsualizacion antes de enviar fotos, videos o documentos */}
      <Dialog open={!!preview} onClose={closePreview} fullWidth maxWidth="sm">
        <DialogContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {preview?.files.map((f, i) => (
            <Box key={i} sx={{ maxWidth: preview.files.length > 1 ? 140 : '100%' }}>
              {f.kind === 'photo' && <Box component="img" src={f.url} alt="" sx={{ maxWidth: '100%', maxHeight: 360, borderRadius: 1 }} />}
              {f.kind === 'video' && <Box component="video" src={f.url} controls sx={{ maxWidth: '100%', maxHeight: 360, borderRadius: 1 }} />}
              {f.kind === 'file' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <InsertDriveFileIcon color="primary" />
                  <Box>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{f.file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatSize(f.file.size)}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
          <TextField fullWidth size="small" placeholder="Anade un comentario..." sx={{ mt: 1 }}
            value={preview?.caption || ''} onChange={(e) => setPreview((p) => ({ ...p, caption: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Cancelar</Button>
          <Button variant="contained" endIcon={<SendIcon />} onClick={sendPreview}>Enviar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
